import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import DatePicker from '../../../../templates/components/DatePicker';
import Module from '../../../../templates/components/Module';
import { store } from '../../../../../context/StateProvider';
import ProviderDashboardService from '../../../../../services/api/provider-dashboard.service';
import { TransactionStatusMapEnum } from '../../../../../common/enum/Conversions/transaction-status-map.enum';
import Utilities from '../../../../../services/commonservice/utilities';

const PaymentsDashboardReport = () => {
    const { state } = useContext(store);
    const [startDate, setStartDate] = useState(moment().startOf('day'));
    const [endDate, setEndDate] = useState(moment().endOf('day'));
    const [isLoading, setIsLoading] = useState(false);
    const [locationId, setLocationId] = useState(state.practiceLocationId || null);
    const [isLocationClearable, setIsLocationClearable] = useState(!(state.providerSelected?.practiceLocationUsers?.length > 0));
    const [initialMessage, setInitialMessage] = useState(true)
    const [groupedData, setGroupedData] = useState({});

    const fetchReport = async () => {
        setGroupedData({})
        setInitialMessage(false)
        setIsLoading(true);
        let reqObj = {
            EndDate: moment(endDate).toISOString(),
            StartDate: moment(startDate).toISOString(),
        };
        if (locationId) {
            reqObj.PracticeLocationId = locationId
        }
        try {
            const response = await ProviderDashboardService.getPaymentReport(reqObj);
            const data = response || [];

            const grouped = {};
            data.forEach(({ section, category, rptTransactionStatusName, totalAmount }) => {
                if (!grouped[section]) grouped[section] = {};
                if (!grouped[section][category]) grouped[section][category] = [];
                grouped[section][category].push({ rptTransactionStatusName, totalAmount });
            });

            setGroupedData(grouped);
        } catch (err) {
            console.error('Failed to fetch report', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReport();
    }, []);

    const renderTable = () => {
        const statusList = (() => {
            const allStatuses = [];

            //each section
            for (const sectionName in groupedData) {
                const section = groupedData[sectionName];

                //each category inside the section
                for (const categoryName in section) {
                    const items = section[categoryName];

                    //rptTransactionStatusName values
                    for (const item of items) {
                        allStatuses.push(item.rptTransactionStatusName);
                    }
                }
            }

            //Remove duplicates
            const uniqueStatuses = [...new Set(allStatuses)];

            const knownStatuses = TransactionStatusMapEnum.filter(e => uniqueStatuses.includes(e.value));
            const unknownStatuses = uniqueStatuses
                .filter(status => !TransactionStatusMapEnum.some(e => e.value === status))
                .map(status => ({ title: status, value: status }));

            return [...knownStatuses, ...unknownStatuses];
        })();

        const orderedSections = (() => {
            const allSections = [];

            //section names
            for (const sectionName in groupedData) {
                allSections.push(sectionName);
            }

            //priorities
            const first = "Regular";
            const second = "Subscription/Payment Plan";
            const last = "Other";

            const ordered = [];

            // Add in order if they exist
            if (groupedData[first]) ordered.push(first);
            if (groupedData[second]) ordered.push(second);

            // Add the remaining (not first, second, or last)
            for (const name of allSections) {
                if (name !== first && name !== second && name !== last) {
                    ordered.push(name);
                }
            }

            // Add 'Other' section at the end if it exists
            if (groupedData[last]) ordered.push(last);

            return ordered;
        })();

        return (
            <div className="w-100 overflow-auto">
                {orderedSections.map((section, sIdx) => {
                    const categories = groupedData[section];
                    const totals = {};
                    statusList.forEach(({ value }) => (totals[value] = 0));

                    return (
                        <div key={`section-table-${sIdx}`} className="mb-5">
                            <table className="w-100 table-auto border">
                                {/* Status Header */}

                                <thead>
                                    {/* Section Title Row */}
                                    <tr>
                                        <th colSpan={statusList.length + 1} className="text-left px-4 py-2">
                                            <span style={{ fontWeight: "500" }}>{section}</span>
                                        </th>
                                    </tr>

                                    <tr>
                                        <td className="border px-4 py-2"></td>
                                        {statusList.map(status => (
                                            <td key={status.value} className="border px-4 py-2 text-right">
                                                <span style={{ fontWeight: "500" }}>{status.title}</span>
                                            </td>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(categories).map(([category, items], cIdx) => {
                                        const rowValues = {};
                                        statusList.forEach(({ value }) => (rowValues[value] = ''));

                                        items.forEach(({ rptTransactionStatusName, totalAmount }) => {
                                            const val = parseFloat(totalAmount);
                                            rowValues[rptTransactionStatusName] = Utilities.toDollar(val);
                                            totals[rptTransactionStatusName] += val;
                                        });

                                        return (
                                            <tr key={`cat-${sIdx}-${cIdx}`}>
                                                <td className="border px-4 py-2"><span style={{ fontWeight: "500" }}>{category}</span></td>
                                                {statusList.map(({ value }, i) => (
                                                    <td key={`val-${i}`} className="border px-4 py-2 text-right">
                                                        {rowValues[value]}
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}

                                    {/* Total Row */}
                                    <tr>
                                        <td className="border px-4 py-2"><span style={{ fontWeight: "500" }}>Total</span></td>
                                        {statusList.map(({ value }, i) => {
                                            const total = totals[value];
                                            return (
                                                <td key={`total-${i}`} className="border px-4 py-2 text-right">
                                                    <span style={{ fontWeight: "500" }}>{total === 0 ? '' : Utilities.toDollar(total)}</span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="row-fluid" id="primary" role="tabpanel" aria-labelledby="primary-tab">
            <div className="row g-3">
                <div className="col-12 m-p-0">
                    <Module title="Filters">
                        <div className="row d-flex">
                            <div className="col-12 row d-flex justify-content-center mx-0 align-items-end report-filters">
                                <DatePicker
                                    locationClearable={isLocationClearable}
                                    setLocationId={setLocationId}
                                    startDate={startDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    setStartDate={setStartDate}
                                />
                                <div className="col-md-auto col-12">
                                    <button
                                        className="btn btn-primary"
                                        onClick={e => {
                                            e.preventDefault();
                                            fetchReport();
                                        }}
                                        title="Pull Report"
                                    >
                                        <i className="icon arrow circle right" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Module>
                </div>
                <div className='col-12 m-p-0'>
                    <Module title="Payment Dashboard">
                        {Object.keys(groupedData).length < 1 ? <div>
                            {initialMessage
                                ? <p>Please submit the report...</p>
                                : isLoading
                                    ? <p>Please be patient while data loads...</p>
                                    : <p>No Results Found</p>

                            }
                        </div> : null}
                        {!isLoading && Object.keys(groupedData).length > 0 && renderTable()}
                    </Module>
                </div>
            </div>
        </div>
    );
};

export default PaymentsDashboardReport;
